/* React Three Fiber scene — reimagined around the actual HEXA hex logo.
   Written without JSX so the @emergentbase/visual-edits babel plugin can't
   inject `x-line-number` etc. onto three.js primitives. */
import React, { useMemo, useRef } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Points, PointMaterial } from "@react-three/drei";
import * as THREE from "three";

const h = React.createElement;

// Public path — set at build time; served by webpack devServer + prod static
const LOGO_URL = "/hexa-icon-hd.png";

/* ------------------------------------------------------------
   Particle field — kept, dimmer, cooler blue
------------------------------------------------------------ */
function ParticleField({ count = 1800 }) {
  const pointsRef = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 10 + Math.random() * 30;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);

  useFrame((_state, delta) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += delta * 0.02;
      pointsRef.current.rotation.x += delta * 0.006;
    }
  });

  return h(
    Points,
    { ref: pointsRef, positions: positions, stride: 3 },
    h(PointMaterial, {
      transparent: true,
      color: "#60a5fa",
      size: 0.035,
      sizeAttenuation: true,
      depthWrite: false,
      opacity: 0.55,
    })
  );
}

/* ------------------------------------------------------------
   HexLogoPlane — a plane textured with the HEXA logo.
   Uses MeshBasicMaterial with alphaTest so the transparent
   background of the PNG doesn't create depth-fighting halos.
------------------------------------------------------------ */
function HexLogoPlane({
  texture,
  size = 1,
  position = [0, 0, 0],
  spinAxis = "y",
  spinSpeed = 0.6,
  wobble = 0,
  opacity = 1,
  emissive = false,
}) {
  const ref = useRef();
  const start = useMemo(() => Math.random() * Math.PI * 2, []);

  useFrame((state, delta) => {
    if (!ref.current) return;
    if (spinAxis === "y") ref.current.rotation.y += delta * spinSpeed;
    else if (spinAxis === "x") ref.current.rotation.x += delta * spinSpeed;
    else if (spinAxis === "z") ref.current.rotation.z += delta * spinSpeed;
    else {
      ref.current.rotation.x += delta * spinSpeed * 0.6;
      ref.current.rotation.y += delta * spinSpeed;
    }
    // Slight bob
    if (wobble) {
      const t = state.clock.elapsedTime;
      ref.current.position.y = position[1] + Math.sin(t + start) * wobble;
    }
  });

  const children = [
    h("planeGeometry", { key: "g", args: [size, size, 1, 1] }),
    h("meshBasicMaterial", {
      key: "m",
      map: texture,
      transparent: true,
      opacity,
      alphaTest: 0.02,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: emissive ? THREE.AdditiveBlending : THREE.NormalBlending,
    }),
  ];

  return h("mesh", { ref, position }, ...children);
}

/* ------------------------------------------------------------
   HeroLogo — the centerpiece: giant, gently rotating logo with a
   soft additive halo plane behind it for glow.
------------------------------------------------------------ */
function HeroLogo({ texture, mouse }) {
  const groupRef = useRef();
  const glowRef = useRef();
  const logoRef = useRef();

  useFrame((state, delta) => {
    if (groupRef.current) {
      // Mouse parallax on the hero
      groupRef.current.rotation.y = THREE.MathUtils.lerp(
        groupRef.current.rotation.y,
        mouse.current.x * 0.35,
        0.05
      );
      groupRef.current.rotation.x = THREE.MathUtils.lerp(
        groupRef.current.rotation.x,
        -mouse.current.y * 0.25,
        0.05
      );
    }
    if (logoRef.current) {
      // Slow z-rotation of the hero logo itself
      logoRef.current.rotation.z += delta * 0.15;
    }
    if (glowRef.current) {
      const pulse = 1 + Math.sin(state.clock.elapsedTime * 1.6) * 0.08;
      glowRef.current.scale.setScalar(pulse);
    }
  });

  return h(
    "group",
    { ref: groupRef, position: [0, 0, 0] },
    // additive glow halo (extra-large, low opacity)
    h(
      "mesh",
      { ref: glowRef, position: [0, 0, -0.2] },
      h("planeGeometry", { args: [8.5, 8.5, 1, 1] }),
      h("meshBasicMaterial", {
        map: texture,
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        alphaTest: 0.02,
      })
    ),
    // main hero logo plane
    h(
      "mesh",
      { ref: logoRef, position: [0, 0, 0] },
      h("planeGeometry", { args: [4.6, 4.6, 1, 1] }),
      h("meshBasicMaterial", {
        map: texture,
        transparent: true,
        opacity: 1,
        depthWrite: false,
        side: THREE.DoubleSide,
        alphaTest: 0.02,
      })
    )
  );
}

/* ------------------------------------------------------------
   OrbitingLogos — a swarm of small logo planes rotating around
   the hero at multiple radii.
------------------------------------------------------------ */
function OrbitingLogos({ texture, count = 10 }) {
  const groupRef = useRef();

  const items = useMemo(() => {
    const list = [];
    for (let i = 0; i < count; i++) {
      const ring = i % 3;                          // 3 ring bands
      const radius = 6 + ring * 2.5;               // 6, 8.5, 11
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.6;
      const y = (Math.random() - 0.5) * 4;
      const size = ring === 0 ? 0.9 : ring === 1 ? 0.65 : 0.5;
      const opacity = ring === 0 ? 0.95 : ring === 1 ? 0.7 : 0.5;
      const speed = 0.15 - ring * 0.03 + Math.random() * 0.05;
      list.push({ radius, angle, y, size, opacity, speed, spinDir: Math.random() > 0.5 ? 1 : -1 });
    }
    return list;
  }, [count]);

  useFrame((_state, delta) => {
    if (groupRef.current) groupRef.current.rotation.y += delta * 0.04;
    if (!groupRef.current) return;
    groupRef.current.children.forEach((child, i) => {
      const cfg = items[i];
      if (!cfg || !child) return;
      cfg.angle += delta * cfg.speed;
      child.position.x = Math.cos(cfg.angle) * cfg.radius;
      child.position.z = Math.sin(cfg.angle) * cfg.radius;
      child.position.y = cfg.y + Math.sin(cfg.angle * 2) * 0.4;
      child.rotation.y += delta * 0.7 * cfg.spinDir;
      child.rotation.x += delta * 0.25 * cfg.spinDir;
    });
  });

  return h(
    "group",
    { ref: groupRef },
    ...items.map((cfg, i) =>
      h(
        "mesh",
        {
          key: i,
          position: [Math.cos(cfg.angle) * cfg.radius, cfg.y, Math.sin(cfg.angle) * cfg.radius],
        },
        h("planeGeometry", { args: [cfg.size, cfg.size, 1, 1] }),
        h("meshBasicMaterial", {
          map: texture,
          transparent: true,
          opacity: cfg.opacity,
          depthWrite: false,
          side: THREE.DoubleSide,
          alphaTest: 0.02,
        })
      )
    )
  );
}

/* ------------------------------------------------------------
   BackgroundGhosts — a few very large, very dim logos deep in Z
   to give the illusion of an infinite hex constellation.
------------------------------------------------------------ */
function BackgroundGhosts({ texture }) {
  const groupRef = useRef();

  useFrame((_state, delta) => {
    if (groupRef.current) groupRef.current.rotation.z += delta * 0.008;
  });

  const positions = [
    { pos: [-14, 8,  -18], size: 6, op: 0.10, spin: 0.05 },
    { pos: [ 16, -6, -22], size: 7, op: 0.08, spin: -0.04 },
    { pos: [-10, -10,-14], size: 4, op: 0.12, spin: 0.06 },
    { pos: [ 18, 10, -16], size: 5, op: 0.10, spin: -0.05 },
  ];

  return h(
    "group",
    { ref: groupRef },
    ...positions.map((p, i) =>
      h(HexLogoPlane, {
        key: i,
        texture,
        size: p.size,
        position: p.pos,
        spinSpeed: p.spin,
        spinAxis: "y",
        opacity: p.op,
        wobble: 0.6,
        emissive: true,
      })
    )
  );
}

/* ------------------------------------------------------------
   Main scene contents
------------------------------------------------------------ */
function SceneContents({ mouse }) {
  const camGroup = useRef();
  const texture = useLoader(THREE.TextureLoader, LOGO_URL);

  // Tune texture once
  useMemo(() => {
    if (texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
    }
  }, [texture]);

  useFrame((_state, delta) => {
    if (camGroup.current) {
      camGroup.current.rotation.y = THREE.MathUtils.lerp(
        camGroup.current.rotation.y,
        mouse.current.x * 0.15,
        0.04
      );
      camGroup.current.rotation.x = THREE.MathUtils.lerp(
        camGroup.current.rotation.x,
        -mouse.current.y * 0.10,
        0.04
      );
    }
  });

  return h(
    "group",
    { ref: camGroup },
    h("ambientLight", { intensity: 0.5 }),
    h("pointLight", { position: [10, 10, 10], intensity: 1.2, color: "#a78bfa" }),
    h("pointLight", { position: [-10, -5, -10], intensity: 0.9, color: "#38bdf8" }),

    h(BackgroundGhosts, { texture }),
    h(OrbitingLogos, { texture, count: 12 }),
    h(HeroLogo, { texture, mouse }),
    h(ParticleField, null)
  );
}

export default function HexScene({ mouseRef }) {
  return (
    <div className="hex-canvas" data-testid="hex-3d-canvas">
      <Canvas
        camera={{ position: [0, 0, 11], fov: 55 }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        {h(SceneContents, { mouse: mouseRef })}
      </Canvas>
    </div>
  );
}
